import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../service/auth/auth.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshDone$ = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  private navigateToLogin(): void {
    this.router.navigate(['/federation/auth/login']);
  }

  // Auth endpoints must never trigger the refresh retry — they handle their own errors
  private isAuthEndpoint(url: string): boolean {
    return url.includes('auth/login') ||
           url.includes('auth/logout') ||
           url.includes('auth/refresh');
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Add withCredentials so HttpOnly cookies are sent automatically.
    // Add X-Requested-With for CSRF protection (TASK-1.10).
    const clonedRequest = request.clone({
      withCredentials: true,
      setHeaders: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !this.isAuthEndpoint(request.url)) {
          // TASK-1.11: Try to refresh the token before logging out
          return this.handleTokenRefresh(clonedRequest, next, error);
        }
        // Working federation became invalid during the session (deleted / deactivated elsewhere): back to "no federation".
        if (error.status === 403 && error.error?.code === 'FEDERATION_REQUIRED' && !this.authService.needsFederation()) {
          this.authService.dropFederation().subscribe(() => this.router.navigateByUrl(this.authService.homeWithoutFederation()));
        }
        return throwError(() => error);
      })
    );
  }

  private handleTokenRefresh(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      // Another refresh is in progress — wait for it, then retry
      return this.refreshDone$.pipe(
        filter(done => done),
        take(1),
        switchMap(() => next.handle(request))
      );
    }

    this.isRefreshing = true;
    this.refreshDone$.next(false);

    return this.authService.refreshToken().pipe(
      switchMap(() => {
        this.isRefreshing = false;
        this.refreshDone$.next(true);
        return next.handle(request); // Retry original request with new cookie
      }),
      catchError((refreshError) => {
        this.isRefreshing = false;
        // Refresh failed — clear local state WITHOUT making another HTTP call
        this.authService.clearLocalAuth();
        this.navigateToLogin();
        return throwError(() => originalError);
      })
    );
  }
}
